// src/utils/saldosExcelExport.js
import ExcelJS from 'exceljs';
import { doc, getDoc, collection, getDocs, query, orderBy, collectionGroup, where } from 'firebase/firestore';
import { esCasaCaseta, calcularEstadoMoroso } from './morosoUtils';

/**
 * Exporta el libro integral de saldos y finanzas del fraccionamiento en formato Excel (.xlsx) con 3 hojas:
 * 1. 'Saldos': Matriz financiera completa de saldos vigentes compatible con re-importación.
 * 2. 'Cuotas Extraordinarias': Desglose exhaustivo por vivienda de cuotas anuales, alberca y extraordinarias.
 * 3. 'Resumen Financiero': Dashboard ejecutivo con indicadores macroeconómicos y cartera vencida del condominio.
 * 
 * @param {Object} params
 * @param {string} params.fraccionamientoId - ID del fraccionamiento
 * @param {Object} params.db - Instancia de Firestore
 * @param {Function} [params.onProgress] - Callback opcional para reportar avance porcentual (0-100)
 * @returns {Promise<boolean>} Retorna true si se generó y descargó con éxito
 */
export const exportarSaldosCompletosExcel = async ({ fraccionamientoId, db, onProgress }) => {
    if (!fraccionamientoId || !db) {
        throw new Error('fraccionamientoId y db son obligatorios para exportar saldos.');
    }

    if (onProgress) onProgress(10);

    // 1. Obtener datos del fraccionamiento
    let fraccNombre = String(fraccionamientoId);
    let cuotaMensual = 850;
    let mesesPermitidos = 1;
    try {
        const fraccDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
        if (fraccDoc.exists()) {
            const data = fraccDoc.data();
            fraccNombre = String(data.nombre || data.name || fraccionamientoId);
            const cfgFin = data.configuracionFinanciera || {};
            cuotaMensual = Number(cfgFin.montoMensual || cfgFin.cuotaMensual || 850);
            mesesPermitidos = Number(cfgFin.interesesMesesPermitidosDeuda ?? 1);
        }
    } catch (err) {
        console.warn('Aviso al obtener configuración del fraccionamiento:', err);
    }

    if (onProgress) onProgress(25);

    // 2. Obtener todas las casas ordenadas por número
    const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
    const casasQuery = query(casasRef, orderBy('numero'));
    const casasSnap = await getDocs(casasQuery);

    const rawCasas = casasSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) => !esCasaCaseta(c));

    if (onProgress) onProgress(50);

    // 3. Obtener meses pendientes del fraccionamiento mediante Collection Group optimizado
    const mesesSnap = await getDocs(
        query(collectionGroup(db, 'mesesPendientes'), where('fraccionamientoId', '==', String(fraccionamientoId)))
    );

    const mesesPorCasa = new Map();
    mesesSnap.forEach((d) => {
        const data = d.data() || {};
        const casaId = data.casaId || d.ref.parent?.parent?.id;
        if (!mesesPorCasa.has(casaId)) mesesPorCasa.set(casaId, []);
        mesesPorCasa.get(casaId).push({ id: d.id, ...data });
    });

    if (onProgress) onProgress(75);

    // 4. Mapeo estructurado por casa
    const aniosCols = [2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026];

    const processedRows = rawCasas.map((c) => {
        const numero = String(c.numero || c.numeroCasa || '').trim();
        const nombre = String(c.nombre_residente || c.nombreCompleto || c.nombre || 'Residente').trim();
        const saldoFavor = Number(c.saldoFavor || 0);
        const cuotaMantto = Number(c.adeudoCapital || 0);
        const interesTotal = Number(c.adeudoIntereses ?? c.adeudoInteres ?? 0);
        const deudaTotal = Number(c.adeudo || 0);
        const meses = mesesPorCasa.get(c.id) || [];

        // Anualidades
        const anualidadesPagadas = {};
        let adeudoAnualidades = 0;
        aniosCols.forEach((a) => {
            anualidadesPagadas[a] = 1000; // Base histórica por defecto
        });

        let adeudoAlberca = 0;
        let pagadoAlberca = 2800;
        let adeudoOtrasExtras = 0;
        let consumos = Number(c.adeudoAgua || 0);

        meses.forEach((m) => {
            const docId = String(m.id || '').toUpperCase();
            const concepto = String(m.concepto || m.descripcion || '').toUpperCase();
            const pend = m.montoPendiente !== undefined 
                ? Number(m.montoPendiente) 
                : (Number(m.monto || 0) - Number(m.montoPagado || 0));
            const pag = Number(m.montoPagado || 0);

            // Anualidades
            const matchAnual = docId.match(/EXT-ANUAL-(\d{4})/) || concepto.match(/ANUAL\s*(\d{4})/);
            if (matchAnual) {
                const y = parseInt(matchAnual[1], 10);
                if (aniosCols.includes(y)) {
                    const pagadoReal = m.montoPagado !== undefined ? pag : Math.max(0, 1000 - pend);
                    anualidadesPagadas[y] = pagadoReal;
                    if (pend > 0) adeudoAnualidades += pend;
                }
            } else if (docId.includes('ALBERCA') || concepto.includes('ALBERCA')) {
                adeudoAlberca = pend;
                pagadoAlberca = pag;
            } else if (docId.startsWith('EXT-') || m.tipo === 'cuota_extraordinaria') {
                if (pend > 0) adeudoOtrasExtras += pend;
            } else if (m.tipo === 'adeudo_agua' || docId.startsWith('AGUA')) {
                if (pend > 0) consumos += pend;
            }
        });

        const totalPagadoAnuales = aniosCols.reduce((sum, a) => sum + (anualidadesPagadas[a] || 0), 0);
        const totalPagadoExtras = totalPagadoAnuales + pagadoAlberca;
        const extraordinariasTotal = Number(c.cuotasExtras ?? (adeudoAnualidades + adeudoAlberca + adeudoOtrasExtras));

        return {
            numero,
            nombre,
            saldoFavor,
            cuotaMantto,
            interesTotal,
            anualidadesPagadas,
            totalPagadoAnuales,
            adeudoAnualidades,
            pagadoAlberca,
            adeudoAlberca,
            adeudoOtrasExtras,
            extraordinariasTotal,
            consumos,
            deudaTotal,
            totalPagadoExtras,
            esMoroso: (saldoFavor > 0 ? false : calcularEstadoMoroso(deudaTotal, cuotaMensual, mesesPermitidos))
        };
    });

    // 5. Construcción del Workbook con ExcelJS
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Domus Habita - Gestión Fraccionamientos';
    wb.created = new Date();

    // =========================================================================
    // HOJA 1: SALDOS (Matriz de Saldos Vigentes - 100% Compatible Re-Importación)
    // =========================================================================
    const wsSaldos = wb.addWorksheet('Saldos', { views: [{ state: 'frozen', ySplit: 1, showGridLines: true }] });

    const headersSaldos = [
        'numero', 'nombre', 'saldoFavor', 'cuotaMantto', 'interesTotal',
        'Anual 2017', 'Anual 2018', 'Anual 2019', 'Anual 2020', 'Anual 2021',
        'Anual 2022', 'Anual 2023', 'Anual 2024', 'Anual 2025', 'Anual 2026',
        'extraordinarias', 'consumos', 'deudaTotal'
    ];

    const headerRowSaldos = wsSaldos.addRow(headersSaldos);
    headerRowSaldos.height = 25;
    headerRowSaldos.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    headerRowSaldos.alignment = { vertical: 'middle', horizontal: 'center' };
    headerRowSaldos.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E79' } };
        cell.border = {
            top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            bottom: { style: 'medium', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            right: { style: 'thin', color: { argb: 'FFD9D9D9' } }
        };
    });

    // Anchos de columna
    wsSaldos.getColumn(1).width = 12; // numero
    wsSaldos.getColumn(2).width = 34; // nombre
    wsSaldos.getColumn(3).width = 15; // saldoFavor
    wsSaldos.getColumn(4).width = 15; // cuotaMantto
    wsSaldos.getColumn(5).width = 15; // interesTotal
    for (let c = 6; c <= 15; c++) wsSaldos.getColumn(c).width = 13; // Anuales
    wsSaldos.getColumn(16).width = 16; // extraordinarias
    wsSaldos.getColumn(17).width = 14; // consumos
    wsSaldos.getColumn(18).width = 16; // deudaTotal

    // Formato moneda mexicana
    for (let c = 3; c <= 18; c++) {
        wsSaldos.getColumn(c).numFmt = '[$MXN] #,##0.00';
    }

    const dataStartRow = 2;
    processedRows.forEach((r) => {
        const rowVals = [
            r.numero,
            r.nombre,
            r.saldoFavor,
            r.cuotaMantto,
            r.interesTotal,
            r.anualidadesPagadas[2017],
            r.anualidadesPagadas[2018],
            r.anualidadesPagadas[2019],
            r.anualidadesPagadas[2020],
            r.anualidadesPagadas[2021],
            r.anualidadesPagadas[2022],
            r.anualidadesPagadas[2023],
            r.anualidadesPagadas[2024],
            r.anualidadesPagadas[2025],
            r.anualidadesPagadas[2026],
            r.extraordinariasTotal,
            r.consumos,
            r.deudaTotal
        ];

        const row = wsSaldos.addRow(rowVals);
        row.height = 20;

        if (r.saldoFavor > 0) {
            row.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD4EDDA' } };
            row.getCell(3).font = { color: { argb: 'FF155724' }, bold: true };
        }
        if (r.deudaTotal > 0) {
            row.getCell(18).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8D7DA' } };
            row.getCell(18).font = { color: { argb: 'FF721C24' }, bold: true };
        }

        row.eachCell({ includeEmpty: true }, (cell) => {
            cell.border = {
                bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
            };
        });
    });

    const dataEndRow = dataStartRow + processedRows.length - 1;

    // Fila de TOTALES
    const totalsRowVals = ['TOTAL', 'TOTALES GENERALES'];
    for (let c = 3; c <= 18; c++) {
        const colLetter = wsSaldos.getColumn(c).letter;
        totalsRowVals.push({ formula: `SUM(${colLetter}${dataStartRow}:${colLetter}${dataEndRow})` });
    }

    const totalsRow = wsSaldos.addRow(totalsRowVals);
    totalsRow.height = 24;
    totalsRow.font = { bold: true, size: 10, color: { argb: 'FF000000' } };
    totalsRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } };
        cell.border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'double', color: { argb: 'FF000000' } }
        };
    });

    // =========================================================================
    // HOJA 2: CUOTAS EXTRAORDINARIAS (Desglose Exhaustivo por Vivienda)
    // =========================================================================
    const wsExtras = wb.addWorksheet('Cuotas Extraordinarias', { views: [{ state: 'frozen', ySplit: 1, showGridLines: true }] });

    const headersExtras = [
        'numero', 'nombre',
        'Anual 2017', 'Anual 2018', 'Anual 2019', 'Anual 2020', 'Anual 2021',
        'Anual 2022', 'Anual 2023', 'Anual 2024', 'Anual 2025', 'Anual 2026',
        'total_pagado', 'adeudo_anual', 'adeudo_alberca', 'adeudo_extra_18', 'adeudo_total_ext'
    ];

    const headerRowExt = wsExtras.addRow(headersExtras);
    headerRowExt.height = 25;
    headerRowExt.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    headerRowExt.alignment = { vertical: 'middle', horizontal: 'center' };
    headerRowExt.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A235A' } }; // Púrpura ejecutivo
        cell.border = { bottom: { style: 'medium', color: { argb: 'FF000000' } } };
    });

    wsExtras.getColumn(1).width = 12;
    wsExtras.getColumn(2).width = 34;
    for (let c = 3; c <= 17; c++) {
        wsExtras.getColumn(c).width = 14;
        wsExtras.getColumn(c).numFmt = '[$MXN] #,##0.00';
    }

    processedRows.forEach((r) => {
        const extVals = [
            r.numero,
            r.nombre,
            r.anualidadesPagadas[2017],
            r.anualidadesPagadas[2018],
            r.anualidadesPagadas[2019],
            r.anualidadesPagadas[2020],
            r.anualidadesPagadas[2021],
            r.anualidadesPagadas[2022],
            r.anualidadesPagadas[2023],
            r.anualidadesPagadas[2024],
            r.anualidadesPagadas[2025],
            r.anualidadesPagadas[2026],
            r.totalPagadoAnuales,
            r.adeudoAnualidades,
            r.adeudoAlberca,
            r.adeudoOtrasExtras,
            r.extraordinariasTotal
        ];

        const row = wsExtras.addRow(extVals);
        row.height = 20;

        if (r.adeudoAlberca > 0) {
            row.getCell(15).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC7CE' } };
            row.getCell(15).font = { bold: true, color: { argb: 'FF9C0006' } };
        }
        if (r.extraordinariasTotal > 0) {
            row.getCell(17).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2DCDB' } };
            row.getCell(17).font = { bold: true };
        }

        row.eachCell({ includeEmpty: true }, (cell) => {
            cell.border = {
                bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
            };
        });
    });

    const extTotalsVals = ['TOTAL', 'TOTALES RECAUDACIÓN Y ADEUDOS'];
    for (let c = 3; c <= 17; c++) {
        const colLetter = wsExtras.getColumn(c).letter;
        extTotalsVals.push({ formula: `SUM(${colLetter}${dataStartRow}:${colLetter}${dataEndRow})` });
    }

    const totalsExtRow = wsExtras.addRow(extTotalsVals);
    totalsExtRow.height = 24;
    totalsExtRow.font = { bold: true, size: 10 };
    totalsExtRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } };
        cell.border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'double', color: { argb: 'FF000000' } }
        };
    });

    // =========================================================================
    // HOJA 3: RESUMEN FINANCIERO (Dashboard Global de Tesorería)
    // =========================================================================
    const wsResumen = wb.addWorksheet('Resumen Financiero', { views: [{ showGridLines: true }] });

    wsResumen.mergeCells('A1', 'E1');
    const resTitle = wsResumen.getCell('A1');
    resTitle.value = `FRACCIONAMIENTO: ${fraccNombre.toUpperCase()} — BALANCE FINANCIERO EJECUTIVO`;
    resTitle.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
    resTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF203764' } };
    resTitle.alignment = { vertical: 'middle', horizontal: 'center' };
    wsResumen.getRow(1).height = 30;

    wsResumen.getColumn(1).width = 4;
    wsResumen.getColumn(2).width = 40;
    wsResumen.getColumn(3).width = 24;
    wsResumen.getColumn(4).width = 35;
    wsResumen.getColumn(5).width = 22;

    const totalCasas = processedRows.length;
    const totalMorosos = processedRows.filter((r) => r.esMoroso).length;
    const totalAlCorriente = totalCasas - totalMorosos;
    const pctMorosidad = (totalMorosos / totalCasas) * 100;

    const totalCapital = processedRows.reduce((s, r) => s + r.cuotaMantto, 0);
    const totalIntereses = processedRows.reduce((s, r) => s + r.interesTotal, 0);
    const totalAnualesPend = processedRows.reduce((s, r) => s + r.adeudoAnualidades, 0);
    const totalAlbercaPend = processedRows.reduce((s, r) => s + r.adeudoAlberca, 0);
    const totalOtrasExtrasPend = processedRows.reduce((s, r) => s + r.adeudoOtrasExtras, 0);
    const totalExtrasPend = processedRows.reduce((s, r) => s + r.extraordinariasTotal, 0);
    const totalConsumos = processedRows.reduce((s, r) => s + r.consumos, 0);
    const totalDeuda = processedRows.reduce((s, r) => s + r.deudaTotal, 0);
    const totalSaldoFavor = processedRows.reduce((s, r) => s + r.saldoFavor, 0);
    const totalRecaudadoExtras = processedRows.reduce((s, r) => s + r.totalPagadoExtras, 0);

    let rIdx = 3;

    // Bloque 1: Padrón
    wsResumen.mergeCells(`B${rIdx}:C${rIdx}`);
    wsResumen.getCell(`B${rIdx}`).value = '1. ESTADO DEL PADRÓN RESIDENCIAL';
    wsResumen.getCell(`B${rIdx}`).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    wsResumen.getCell(`B${rIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E79' } };
    rIdx++;

    const addKpiRow = (label, val, isMoney = false, isPct = false) => {
        wsResumen.getCell(`B${rIdx}`).value = label;
        const vCell = wsResumen.getCell(`C${rIdx}`);
        vCell.value = val;
        vCell.font = { bold: true };
        if (isMoney) vCell.numFmt = '[$MXN] #,##0.00';
        if (isPct) vCell.numFmt = '0.0"%';
        wsResumen.getCell(`B${rIdx}`).border = { bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } } };
        vCell.border = { bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } } };
        rIdx++;
    };

    addKpiRow('Total Viviendas Residenciales', totalCasas);
    addKpiRow('Viviendas al Corriente', totalAlCorriente);
    addKpiRow('Viviendas con Adeudo (Morosos)', totalMorosos);
    addKpiRow('Índice de Morosidad', pctMorosidad / 100, false, true);
    addKpiRow('Cuota Mensual Ordinaria de Mantenimiento', cuotaMensual, true);

    rIdx++;
    // Bloque 2: Cartera Vencida
    wsResumen.mergeCells(`B${rIdx}:C${rIdx}`);
    wsResumen.getCell(`B${rIdx}`).value = '2. CARTERA VENCIDA POR CONCEPTO';
    wsResumen.getCell(`B${rIdx}`).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    wsResumen.getCell(`B${rIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC00000' } };
    rIdx++;

    addKpiRow('Cuota Mantenimiento Ordinario (Capital)', totalCapital, true);
    addKpiRow('Recargos e Intereses por Mora', totalIntereses, true);
    addKpiRow('Cuotas Anuales Pendientes (2017-2026)', totalAnualesPend, true);
    addKpiRow('Cuota Extraordinaria Alberca Pendiente', totalAlbercaPend, true);
    addKpiRow('Otras Cuotas Extraordinarias Pendientes', totalOtrasExtrasPend, true);
    addKpiRow('Total Cuotas Extraordinarias por Cobrar', totalExtrasPend, true);
    addKpiRow('Consumos de Agua Potable Pendientes', totalConsumos, true);

    wsResumen.getCell(`B${rIdx}`).value = 'TOTAL CARTERA VENCIDA DEL FRACCIONAMIENTO';
    wsResumen.getCell(`B${rIdx}`).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    wsResumen.getCell(`B${rIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF800000' } };
    const totDebtCell = wsResumen.getCell(`C${rIdx}`);
    totDebtCell.value = totalDeuda;
    totDebtCell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
    totDebtCell.numFmt = '[$MXN] #,##0.00';
    totDebtCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF800000' } };
    rIdx += 2;

    // Bloque 3: Tesorería y Fondos
    wsResumen.mergeCells(`B${rIdx}:C${rIdx}`);
    wsResumen.getCell(`B${rIdx}`).value = '3. SALDOS A FAVOR Y RECAUDACIÓN';
    wsResumen.getCell(`B${rIdx}`).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    wsResumen.getCell(`B${rIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF375623' } };
    rIdx++;

    addKpiRow('Total Saldo a Favor de Residentes', totalSaldoFavor, true);
    addKpiRow('Recaudación Cuotas Extraordinarias (Cubiertas)', totalRecaudadoExtras, true);

    if (onProgress) onProgress(90);

    // 6. Generación del Buffer y Descarga en Navegador
    const buffer = await wb.xlsx.writeBuffer();
    const blobOut = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blobOut);
    const a = document.createElement('a');
    a.href = url;
    const cleanNombre = String(fraccNombre).trim().replace(/[/\\?%*:|"<>]/g, '').replace(/\s+/g, '_');
    const fechaHoy = new Date().toISOString().slice(0, 10);
    a.download = `saldos_fraccionamiento_${cleanNombre}_${fechaHoy}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    if (onProgress) onProgress(100);
    return true;
};
